import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Tooltip, TablePagination, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Grid,
  MenuItem, Select, FormControl, InputLabel, Box, Typography, Checkbox,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import SearchFilterBar from '../../components/Common/SearchFilterBar';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import {
  getMyBooksApi, createBookApi, updateBookApi, deleteBookApi, bulkDeleteBooksApi,
  getBookFilterOptionsApi, exportMyBooksApi,
} from '../../api/books';
import { LANGUAGES, TARGET_SEGMENTS, GENRES } from '../../utils/constants';
import { hasActiveQuery, buildFilterParams } from '../../utils/bookFilterHelpers';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

const emptyBook = {
  isbn: '', author_name: '', title: '', discipline: '', genre: '', description: '',
  pages: '', language: 'English', target_segment: '', publication_date: '', price: '',
};

export default function PublisherBooks() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [form, setForm] = useState(emptyBook);
  const [editId, setEditId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [selected, setSelected] = useState([]);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const queryActive = hasActiveQuery(search, filters);

  const fetchBooks = useCallback(async () => {
    if (!hasActiveQuery(search, filters)) {
      setBooks([]);
      setTotal(0);
      setSelected([]);
      return;
    }
    const res = await getMyBooksApi({ page: page + 1, page_size: rowsPerPage, search, ...buildFilterParams(filters) });
    setBooks(res.data.results || res.data);
    setTotal(res.data.count || res.data.length);
    setSelected([]);
  }, [page, rowsPerPage, search, filters]);

  useEffect(() => { fetchBooks(); }, [fetchBooks]);
  useEffect(() => {
    getBookFilterOptionsApi().then((r) => {
      setDisciplines(r.data.disciplines || []);
      setLanguages(r.data.languages || []);
    }).catch(() => {});
  }, []);

  const handleExport = async (fileFormat) => {
    const res = await exportMyBooksApi({ ...buildFilterParams(filters), search, export_format: fileFormat });
    downloadBlob(res.data, `my_books_export.${fileFormat}`);
  };

  const openAdd = () => { setForm(emptyBook); setEditId(null); setCoverFile(null); setSaveError(''); setDialogOpen(true); };
  const openEdit = (b) => {
    setForm({ ...b, publication_date: b.publication_date || '' });
    setEditId(b.id); setCoverFile(null); setSaveError(''); setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaveError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined && !['id', 'publisher', 'publisher_name', 'publisher_company', 'publisher_mobile', 'uploaded_by_name', 'upload_source', 'created_at', 'cover_image'].includes(k))
          fd.append(k, v);
      });
      if (coverFile) fd.append('cover_image', coverFile);
      if (editId) await updateBookApi(editId, fd);
      else await createBookApi(fd);
      setDialogOpen(false);
      fetchBooks();
    } catch (err) {
      const d = err.response?.data;
      setSaveError(d?.isbn?.[0] || d?.non_field_errors?.[0] || JSON.stringify(d) || 'Save failed.');
    }
  };

  const handleDelete = async () => {
    await deleteBookApi(confirmDelete);
    setConfirmDelete(null);
    fetchBooks();
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteBooksApi(selected);
    } catch (err) {
      console.error('Bulk delete failed:', err);
    }
    setConfirmBulkDelete(false);
    fetchBooks();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selected.length === books.length) setSelected([]);
    else setSelected(books.map((b) => b.id));
  };

  const bookFilters = [
    { key: 'discipline', label: 'Discipline', type: 'select', multiple: true, options: disciplines },
    { key: 'language', label: 'Language', type: 'select', multiple: true, options: languages },
    { key: 'target_segment', label: 'Target Segment', type: 'select', multiple: true, options: TARGET_SEGMENTS },
    { key: 'author_name', label: 'Author Name', type: 'text' },
  ];

  return (
    <Layout title="My Books">
      <PageHeader
        title="My Books"
        breadcrumbs={[{ label: 'Publisher' }, { label: 'Books' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('csv')}>Export CSV</Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('xlsx')}>Export Excel</Button>
            <Button variant="outlined" onClick={() => navigate('/publisher/upload')}>Upload via Excel</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>Add Book</Button>
          </Box>
        }
      />
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <SearchFilterBar searchPlaceholder="Search ISBN, title, author..." filters={bookFilters} onSearch={setSearch} onFilter={setFilters} />
            </Box>
            {selected.length > 0 && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={() => setConfirmBulkDelete(true)}
                sx={{ ml: 2, whiteSpace: 'nowrap' }}
              >
                Delete Selected ({selected.length})
              </Button>
            )}
          </Box>
          {!queryActive ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <FilterAltIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                Select at least one filter or search term above to view your books.
              </Typography>
            </Box>
          ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < books.length}
                    checked={books.length > 0 && selected.length === books.length}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>ISBN</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Language</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {books.map((b) => (
                <TableRow key={b.id} hover selected={selected.includes(b.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.includes(b.id)} onChange={() => toggleSelect(b.id)} />
                  </TableCell>
                  <TableCell>{b.isbn}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</TableCell>
                  <TableCell>{b.author_name}</TableCell>
                  <TableCell>{b.language}</TableCell>
                  <TableCell>₹{b.price}</TableCell>
                  <TableCell>{b.publication_date || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEdit(b)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setConfirmDelete(b.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
          {queryActive && <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]} />}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? 'Edit Book' : 'Add New Book'}</DialogTitle>
        <DialogContent dividers>
          {saveError && <Box sx={{ color: 'error.main', mb: 1, fontSize: 13 }}>{saveError}</Box>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="ISBN" value={form.isbn || ''} onChange={(e) => setForm({ ...form, isbn: e.target.value })} required /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Author Name" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} required /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Discipline" value={form.discipline || ''} onChange={(e) => setForm({ ...form, discipline: e.target.value })} placeholder="e.g. Computer Science, Mathematics" /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth><InputLabel>Genre</InputLabel>
                <Select label="Genre" value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })}>
                  {GENRES.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth><InputLabel>Language</InputLabel>
                <Select label="Language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                  {LANGUAGES.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Pages" type="number" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Price (₹)" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Publication Date" type="date" value={form.publication_date} onChange={(e) => setForm({ ...form, publication_date: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth><InputLabel>Target Segment</InputLabel>
                <Select label="Target Segment" value={form.target_segment || ''} onChange={(e) => setForm({ ...form, target_segment: e.target.value })}>
                  {TARGET_SEGMENTS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">Cover Image</Typography>
                <input type="file" accept="image/*" onChange={(e) => setCoverFile(e.target.files[0])} style={{ display: 'block', marginTop: 4 }} />
              </Box>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Synopsis / Description" multiline rows={3} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={!!confirmDelete} title="Delete Book" message="This will permanently delete the book."
        onConfirm={handleDelete} onCancel={() => setConfirmDelete(null)} confirmText="Delete" />

      <ConfirmDialog
        open={confirmBulkDelete}
        title="Delete Selected Books"
        message={`This will permanently delete ${selected.length} selected book(s). This action cannot be undone.`}
        onConfirm={handleBulkDelete}
        onCancel={() => setConfirmBulkDelete(false)}
        confirmText="Delete All"
      />
    </Layout>
  );
}
